import { Component } from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import autoBind from 'react-autobind';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';

import { isMobile } from 'react-device-detect';
import * as storyFiltersActions from '../../actions/storyFilters.action';
import * as storyActions from '../../actions/stories.actions';
import * as watchlistActions from '../../actions/watchlistActions';
import * as searchActions from '../../actions/searchActions';
import permissionsDecorator from '../../decorators/permissionsDecorator';
import withComponentName from '../../decorators/withComponentName';
import * as preferencesActions from '../../actions/preferencesActions';

import DropdownRadioFilter from './DropdownRadioFilter';
import SourceFilter from './SourceFilter';
import ScoreFilter from './ScoreFilter';
import LanguageFilter from './LanguageFilter';
import OtherFilter from './OtherFilter';
import TimePeriodFilter from './TimePeriodFilter';
import SentimentFilter from './SentimentFilter';
import {
  compareResetFilters, getDifferentResetFilters,
  getResetValuesForFilters,
} from '../../helpers/helpers';
import { clearSavedStoryFiltersStates } from '../../services/localStorage';
import StorageSvc from '../../services/StorageSvc';

import {
  initialState,
  refreshValues,
  orderValues,
  sourceValues,
  extraSourceValues,
  refreshRateConversion,
} from '../../data/filters/storyFilters';

import { GOLD, TWEETS } from '../../data/permissions';
import { REFRESH_FILTER } from '../../data/features';

import PiwikContext from '../../services/piwikContext';

import Styles from './story-filters-styles.module.scss';

import 'react-datepicker/dist/react-datepicker.css';
import ResetButton from './ResetButton';

import { EARLIEST_SEARCH_DATE_GOLD_USER } from '../../data/environment';

const cx = classNames.bind(Styles);

export class StoryFilters extends Component {
  constructor(props) {
    super(props);
    this.state = {
      differentFiltersValues: [],
      isShowMajorSources: false,
      isLoading: true,
    };
    autoBind(this);
  }

  componentDidMount() {
    const { onRef, token, storyFilters } = this.props;

    // this for a passing original component (not wrapped one) to parent to call its method direct
    if (typeof onRef === 'function') onRef(this);

    if (token) {
      clearSavedStoryFiltersStates();
    }
    const showMajorSources = JSON.parse(StorageSvc.getItem('ShowMajorSources'))?.status;
    if (showMajorSources !== undefined) {
      this.setState({
        isShowMajorSources: showMajorSources,
      });
    }
    if (storyFilters.filtersInitialized) {
      this.setDifferentFiltersValues();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { storyFilters } = this.props;
    const showMajorSources = JSON.parse(StorageSvc.getItem('ShowMajorSources'))?.status;
    if (
      (
        prevProps.storyFilters.filtersInitialized !== undefined
        && !compareResetFilters(prevProps.storyFilters, storyFilters)
        && storyFilters.conflictsChecked
      )
      || (
        showMajorSources !== undefined
        && prevState.isShowMajorSources !== showMajorSources
      )
    ) {
      if (showMajorSources !== undefined) {
        this.setState({
          isShowMajorSources: showMajorSources,
          isLoading: true,
        });
      }
      this.setDifferentFiltersValues();
    }
  }

  handleFilterClose() {
    const { actions } = this.props;
    actions.toggleStoryFilters(false);
  }

  onFreezeStories(status) {
    const { storyFilters, actions } = this.props;
    if (storyFilters.refresh_rate !== 'never' && Number(storyFilters.refresh_rate)) {
      actions.freezeStories(status);
    }
  }

  async setDifferentFiltersValues() {
    try {
      let differentData = {};
      const {
        accessLevels,
        storyFilters,
        width,
        isPremiumPage,
        token,
      } = this.props;
      const userNewsfeedCompactView = isMobile && width < 768 ? (
        storyFilters.user_newsfeed_compactview
      ) : (
        storyFilters.user_newsfeed_compactview_web
      );
      const resetValues = {
        ...getResetValuesForFilters(accessLevels, userNewsfeedCompactView),
        paywall: true,
        registrationRequired: true,
        isPremiumLabelsEnabled: true,
      };
      const localRequiredSourceVal = JSON.parse(StorageSvc.getItem('state'))?.storyFilters?.required_source;
      const paywallValue = localRequiredSourceVal?.paywall;
      const registrationRequiredVal = localRequiredSourceVal?.registrationRequired;
      const isPremiumLabelsEnabled = JSON.parse(StorageSvc.getItem('showPremiumLabels'))?.status;
      if (token) {
        const newData = {
          ...storyFilters,
          languages: storyFilters.lang,
          categories: storyFilters.categories?.split(',').filter((cat) => !['c', 'i', 'e'].includes(cat)).join(',') || '',
          paywall: paywallValue,
          registrationRequired: registrationRequiredVal,
          isPremiumLabelsEnabled: isPremiumLabelsEnabled === undefined
            ? true : isPremiumLabelsEnabled,
          refresh_rate: refreshRateConversion[storyFilters.refresh_rate],
        };

        const differentFilterData = getDifferentResetFilters(
          resetValues,
          newData,
          accessLevels,
        );
        let newDifferentFilterData = [];
        differentFilterData.forEach((filter) => {
          if (filter === 'major_sources_only') {
            differentData = {
              key: 'major_sources_only',
              currentValue: true,
              resetValue: false,
            };
          } else if (filter === 'selected_time_filter' && newData[filter]?.toLowerCase() === 'time_period') {
            differentData = {
              key: filter,
              currentValue: storyFilters.time_period,
              resetValue: resetValues[filter],
            };
          } else {
            differentData = {
              key: filter,
              currentValue: newData[filter],
              resetValue: resetValues[filter],
            };
          }
          if ((filter === 'major_sources_only' || filter === 'premium_content_only' || filter === 'registrationRequired' || filter === 'paywall' || filter === 'categories' || filter === 'twitter_following_only' || filter === 'with_links') && isPremiumPage) {
            return;
          }
          newDifferentFilterData.push(differentData);
        });
        [...newDifferentFilterData].forEach((fData) => {
          if (fData.key === 'sentiment_filter') {
            if (fData.currentValue === 'range') {
              newDifferentFilterData = newDifferentFilterData.filter((data) => data.key !== 'sentiment_categories');
            }
          }
        });
        this.setState({
          differentFiltersValues: newDifferentFilterData,
          isLoading: false,
        });
      } else {
        const newData = {
          ...JSON.parse(StorageSvc.getItem('state'))?.storyFilters,
          paywall: paywallValue,
          registrationRequired: registrationRequiredVal,
          isPremiumLabelsEnabled: isPremiumLabelsEnabled === undefined
            ? true : isPremiumLabelsEnabled,
        };
        const differentFilterData = getDifferentResetFilters(resetValues, newData, accessLevels);
        const newDifferentFilterData = [];
        differentFilterData.forEach((filter) => {
          if (filter === 'major_sources_only') {
            differentData = {
              key: 'major_sources_only',
              currentValue: true,
              resetValue: false,
            };
          } else if (filter === 'selected_time_filter' && newData[filter]?.toLowerCase() === 'time_period') {
            differentData = {
              key: filter,
              currentValue: storyFilters.time_period,
              resetValue: resetValues[filter],
            };
          } else {
            differentData = {
              key: filter,
              currentValue: newData[filter],
              resetValue: resetValues[filter],
            };
          }
          newDifferentFilterData.push(differentData);
        });
        this.setState({
          differentFiltersValues: newDifferentFilterData,
          isLoading: false,
        });
      }
    } catch (err) {
      console.log(err);
    }
  }

  render() {
    const {
      actions,
      activeWatchlist,
      token,
      watchlistRefresh,
      stories,
      storyFilters,
      user,
      isSearch,
      rbi,
      piwikEnabled,
      disabled,
      accessLevels,
      activeWatchlistTopics,
      permissions,
      filterOpened,
      width,
      isSimpleLayout,
      onSimplifiedViewTabsClick,
      hasBigScreenDesign,
      isSentimentLocation,
      isDisabled,
      bookmarksPage,
      additionalStyles,
    } = this.props;
    const { differentFiltersValues, isLoading } = this.state;
    const {
      earliest_search_date: earliestSearchDate,
      search_period_duration: searchPeriodDuration,
      features = [],
    } = permissions;

    const searchDatePeriodFilter = accessLevels.includes(GOLD)
      ? EARLIEST_SEARCH_DATE_GOLD_USER
      : earliestSearchDate;

    return (
      <>
        {filterOpened && (
          <div
            className={cx('story-filters', 'story-filters-holder', {
              rbi,
              'is-search': isSearch,
              premium: accessLevels.includes(GOLD),
              big_screen: hasBigScreenDesign,
            }, ...additionalStyles)}
          >

            <div
              className={cx('story-filter__close-btn')}
              onClick={this.handleFilterClose}
            >
              <FontAwesomeIcon icon={faXmark} />
            </div>
            <DropdownRadioFilter
              ref={(el) => { this.refreshFilter = el; }}
              type="refresh"
              dropdownTitle="Autorefresh"
              width={width}
              values={refreshValues}
              action={actions.refreshEvery}
              storiesLoading={stories.loading}
              watchlistRefresh={watchlistRefresh}
              activeWatchlist={activeWatchlist}
              token={token}
              actions={actions}
              storyFilters={storyFilters}
              freezeStories={this.onFreezeStories}
              savedFilterValue={storyFilters.refresh_rate}
              rbi={rbi}
              piwikEnabled={piwikEnabled}
              activeWatchlistTopics={activeWatchlistTopics}
              refreshFeature={features.includes(REFRESH_FILTER)}
              accessLevels={accessLevels}
              isSimpleLayout
            />

            <DropdownRadioFilter
              ref={(el) => { this.orderFilter = el; }}
              type="order"
              dropdownTitle={width >= 768 ? 'order' : 'Story Order'}
              values={orderValues}
              action={actions.filterByStoryOrder}
              freezeStories={this.onFreezeStories}
              savedFilterValue={storyFilters.order_by}
              rbi={rbi}
              piwikEnabled={piwikEnabled}
              token={token}
              width={width}
              isSearch={isSearch}
              accessLevels={accessLevels}
              isSimpleLayout={isSimpleLayout}
            />

            <TimePeriodFilter
              ref={(el) => { this.periodFilter = el; }}
              type="period"
              dropdownTitle={width >= 768 ? 'period' : 'Time Period'}
              disabled={disabled}
              defaultSelected={storyFilters.time_filter}
              initialStateValue={initialState.time_filter}
              action={actions.filterByTimePeriod}
              token={token}
              freezeStories={this.onFreezeStories}
              savedFilterValue={{
                time_filter: storyFilters.time_filter,
                selected_time_filter: storyFilters.selected_time_filter,
                start_datetime: storyFilters.start_datetime,
                end_datetime: storyFilters.end_datetime,
                time_period: storyFilters.time_period,
              }}
              rbi={rbi}
              piwikEnabled={piwikEnabled}
              width={width}
              accessLevels={accessLevels}
              features={features}
              earliestSearchDate={moment(searchDatePeriodFilter)}
              searchPeriodDuration={searchPeriodDuration}
              hasBigScreenDesign={hasBigScreenDesign}
              isSentimentLocation={isSentimentLocation}
            />

            <PiwikContext.Consumer>
              {(value) => (
                <SourceFilter
                  ref={(el) => { this.sourceFilter = el; }}
                  values={[...sourceValues(storyFilters.categories, token)]}
                  token={token}
                  extraValues={[...extraSourceValues]}
                  stateValue={storyFilters.categories}
                  initialStateValue={initialState.categories}
                  dropdownTitle="Content"
                  user={user}
                  action={actions.filterByCategories}
                  extraAction={actions.filterByRequiredSources}
                  advancedAction={actions.filterByAdvancedSettings}
                  freezeStories={this.onFreezeStories}
                  rbi={rbi}
                  width={width}
                  piwikEnabled={value.piwikEnabled}
                  filterByPremiumContent={actions.filterByPremiumContent}
                  sourcesEnabled={value.sourcesEnabled}
                  hasBigScreenDesign={hasBigScreenDesign}
                  isDisabled={isDisabled}
                />
              )}
            </PiwikContext.Consumer>

            <ScoreFilter
              ref={(el) => { this.scoreFilter = el; }}
              dropdownTitle="Score"
              width={width}
              defaultSelected={storyFilters.min_score}
              action={actions.filterByMinScore}
              freezeStories={this.onFreezeStories}
              savedFilterValue={storyFilters.min_score}
              rbi={rbi}
              isSearch={isSearch}
              piwikEnabled={piwikEnabled}
              token={token}
              hasBigScreenDesign={hasBigScreenDesign}
            />

            <LanguageFilter
              ref={(el) => { this.languageFilter = el; }}
              dropdownTitle="Languages"
              action={actions.filterByLanguage}
              actions={actions}
              translationValue={storyFilters.translate_all}
              translateAction={actions.showTranslation}
              selectedAllLanguages={storyFilters.all_languages}
              defaultSelectedLanguages={initialState.languages}
              toggleAllLanguagesSelected={actions.toggleAllLanguagesSelected}
              freezeStories={this.onFreezeStories}
              savedFilterValue={{
                lang: storyFilters.lang,
                all_languages: storyFilters.all_languages,
              }}
              width={width}
              rbi={rbi}
              piwikEnabled={piwikEnabled}
              token={token}
              hasBigScreenDesign={hasBigScreenDesign}
              hideTranslationBar={false}
            />

            <SentimentFilter
              ref={(el) => { this.sentimentFilter = el; }}
              rbi={rbi}
              width={width}
              accessLevels={accessLevels}
              actions={actions}
              freezeStories={this.onFreezeStories}
              savedFilterValue={{
                sentiment_filter: storyFilters.sentiment_filter,
                sentiment_categories: storyFilters.sentiment_categories,
                sentiment_down_limit: storyFilters.sentiment_down_limit,
                sentiment_up_limit: storyFilters.sentiment_up_limit,
              }}
              hasBigScreenDesign={hasBigScreenDesign}
              isSimpleLayout={isSimpleLayout}
              isSentimentLocation={isSentimentLocation}
              piwikEnabled={piwikEnabled}
            />

            <OtherFilter
              dropdownTitle="News feed layout"
              width={width}
              actions={actions}
              freezeStories={this.onFreezeStories}
              filterValues={{
                expand_stories: storyFilters.expand_stories,
                group_similar_stories: storyFilters.group_similar_stories,
                show_similar_stories_in_feed: storyFilters.show_similar_stories_in_feed,
                user_newsfeed_view_images_web: storyFilters.user_newsfeed_view_images_web,
                user_newsfeed_view_description_web:
                  storyFilters.user_newsfeed_view_description_web,
                user_newsfeed_compactview_web: storyFilters.user_newsfeed_compactview_web,
                user_newsfeed_compactview: storyFilters.user_newsfeed_compactview,
                story_preview: storyFilters.story_preview,
                user_newsfeed_view_web: storyFilters.user_newsfeed_view_web,
                user_newsfeed_view: storyFilters.user_newsfeed_view,
                tiles_view: storyFilters.tiles_view,
                summarization_enabled: storyFilters.summarization_enabled,
                user_newsfeed_advanced_view_web: storyFilters.user_newsfeed_advanced_view_web,
                isPremiumLabelsEnabled: storyFilters.isPremiumLabelsEnabled,
              }}
              rbi={rbi}
              token={token}
              piwikEnabled={piwikEnabled}
              onSimplifiedViewTabsClick={onSimplifiedViewTabsClick}
              hasBigScreenDesign={hasBigScreenDesign}
              isSimpleLayout={isSimpleLayout}
              bookmarksPage={bookmarksPage}
            />
            <ResetButton
              className={cx('story-filters_btn--reset')}
              variant="default"
              differentFiltersValues={differentFiltersValues}
              onSimplifiedViewTabsClick={onSimplifiedViewTabsClick}
              resetLanguages={this.languageFilter}
              resetSourceFilters={this.sourceFilter}
              isLoading={isLoading}
              piwikEnabled={piwikEnabled}
              hasBigScreenDesign={hasBigScreenDesign}
            />
          </div>
        )}
      </>
    );
  }
}

StoryFilters.defaultProps = {
  isSearch: false,
  rbi: false,
  additionalStyles: [],
};

function mapStateToProps(state) {
  const { permissions, permissionsInitialized } = state.subscriptions;
  const { access_levels: accessLevels } = permissions;

  const disabled = !accessLevels.includes(TWEETS);

  return {
    stories: state.stories,
    storyFilters: state.storyFilters,
    token: state.watchlistReducer.userToken,
    activeWatchlist: state.watchlistReducer.activeWatchlist,
    accessLevels,
    permissionsInitialized,
    disabled,
    permissions,
    activeWatchlistTopics: state.watchlistReducer.activeWatchlistTopics,
    filterOpened: state.userPreferencesReducer.filterOpened,
    width: state.common.width,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      ...storyFiltersActions,
      ...storyActions,
      ...watchlistActions,
      ...searchActions,
      ...preferencesActions,
    }, dispatch),
  };
}

export default withComponentName(
  connect(mapStateToProps, mapDispatchToProps)(permissionsDecorator(StoryFilters)),
);
